// DraggableButtonContext.tsx
import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { LOCAL_STORAGE_NAMES } from '../constants/constants';
import { readViewportCssSize } from '../utils/displaySize';

type DraggableButtonContextType = {
  positions: {
    button1: number;
    button2: number;
  };
  setPosition: (button: 'button1' | 'button2', pos: number) => void;
};

const DraggableButtonContext = createContext<DraggableButtonContextType | undefined>(undefined);

export const DraggableButtonProvider = ({ children }: { children: React.ReactNode }) => {
  const getInitialPositions = () => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_NAMES.dragabbleButtonsPosition);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to parse saved positions', e);
    }
    // fallback defaults
    const viewportHeight = readViewportCssSize().height;
    return {
      button1: viewportHeight * 0.5,
      button2: viewportHeight * 0.62,
    };
  };

  const [positions, setPositions] = useState(getInitialPositions);

  // persist whenever positions change
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_NAMES.dragabbleButtonsPosition, JSON.stringify(positions));
  }, [positions]);

  const setPosition = (button: 'button1' | 'button2', pos: number) => {
    setPositions((prev: { button1: number; button2: number }) => ({
      ...prev,
      [button]: pos,
    }));
  };

  const value = useMemo(() => ({ positions, setPosition }), [positions]);

  return (
    <DraggableButtonContext.Provider value={value}>{children}</DraggableButtonContext.Provider>
  );
};

export const useDraggableButton = () => {
  const context = useContext(DraggableButtonContext);
  if (!context) {
    throw new Error('useDraggableButton must be used within DraggableButtonProvider');
  }
  return context;
};
